/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable no-shadow */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { SafeHtml } from '@angular/platform-browser';
import {
  Config,
  CurrencyRate,
  TableAndRestaurant,
  WaiterOption
} from '../models/config.model';
import {
  ItemFilter,
  Menu
} from '../models/menu.model';
import {
  CartConfig,
  GetOrder,
  MulesoftRoomCheckResponse,
  Order,
  PickupFormField
} from '../models/cart.model';
import {
  AdyenSessionResponse,
  AdyenValidatePayload,
  ApplePayPayload,
  ApplePayPaymentPayload,
  CheckoutPayload,
  CheckoutResponse,
  CheckoutV2SessionResponse,
  CheckoutV2ValidatePayload,
  CybersourceSessionResponse,
  CybersourceValidatePayload,
  EzeTapSessionDetails,
  EzeTapStatusResponse,
  GeideaCheckoutSession,
  GeideaValidationPayload,
  GetOrders,
  GooglePayPaymentDetails,
  MastercardPayload,
  MastercardResponse,
  MastercardValidationPayload,
  NoonPaymentsPayload,
  NoonPaymentsResponse,
  PayfortSignature,
  Transaction,
} from '../models/payment.model';
import {
  ChequeSyncPayload,
  PayAtTableApplePayPayload,
  PayAtTableCalculatePayload,
  PayAtTableGooglePayPayload,
  PayAtTableNiPayload,
  PayAtTableResponse
} from '../models/pay-at-table.model';
import { VoucherPayload, VoucherResponse } from '../models/voucher.model';
import { NotificationPayload } from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.urls.api;
  }

  public getConfig(tableId: string): Observable<Config> {
    return this.httpClient.get<Config>(`${this.baseUrl}/config/${tableId}`);
  }

  public getMenuCached(zoneId: string): Observable<Menu> {
    return this.httpClient.get<Menu>(`${this.baseUrl}/menu/${zoneId}/cache`);
  }

  public getTableAndRestaurant(tableId: string): Observable<TableAndRestaurant> {
    return this.httpClient.get<TableAndRestaurant>(`${this.baseUrl}/table/${tableId}`);
  }

  public orderItems(tableId: string, order: Order): Observable<GetOrder> {
    return this.httpClient.post<GetOrder>(
      `${this.baseUrl}/order/table/${tableId}`,
      order
    );
  }

  public getOrder(orderId: string): Observable<GetOrder> {
    return this.httpClient.get<GetOrder>(`${this.baseUrl}/order/${orderId}`);
  }

  public cancelOrder(orderId: string): Observable<boolean> {
    return this.httpClient.put<boolean>(
      `${this.baseUrl}/order/${orderId}/cancel`,
      {}
    );
  }

  public getOrders(userId: string, restaurantId: string): Observable<GetOrders> {
    const url = `${this.baseUrl}/order/user/${userId}?restaurant_id=${restaurantId}`;
    return this.httpClient.get<GetOrders>(url);
  }

  public getPayAtTableOrders(
    restaurantId: string,
    payload: VoucherPayload | PayAtTableCalculatePayload
  ): Observable<PayAtTableResponse> {
    const url = `${this.baseUrl}/checkout/postpaid/${restaurantId}`;
    return this.httpClient.post<PayAtTableResponse>(url, payload);
  }

  public getChequeLock(tableId: string): Observable<ChequeSyncPayload> {
    // Not /lock — that only accepts POST/DELETE. We just read `.lock` off this.
    return this.httpClient.get<ChequeSyncPayload>(
      `${this.baseUrl}/cheque/table/${tableId}`
    );
  }

  public acquireChequeLock(tableId: string, userId: string): Observable<ChequeSyncPayload> {
    return this.httpClient.post<ChequeSyncPayload>(
      `${this.baseUrl}/cheque/table/${tableId}/lock`,
      { user_id: userId }
    );
  }

  public releaseChequeLock(tableId: string, userId: string): Observable<ChequeSyncPayload> {
    return this.httpClient.delete<ChequeSyncPayload>(
      `${this.baseUrl}/cheque/table/${tableId}/lock`,
      { body: { user_id: userId } }
    );
  }

  public sendNotification(notificationPayload: NotificationPayload): Observable<boolean> {
    return this.httpClient.post<boolean>(
      `${this.baseUrl}/notification`,
      notificationPayload
    );
  }

  public getNiSession(
    mastercardPayload:
      | ApplePayPaymentPayload
      | MastercardPayload
      | PayAtTableNiPayload
      | PayAtTableApplePayPayload
      | PayAtTableGooglePayPayload
  ): Observable<MastercardResponse> {
    const url = `${this.baseUrl}/payment/ni/session`;
    return this.httpClient.post<MastercardResponse>(url, mastercardPayload);
  }

  public getCheckoutSession(checkoutPayload: CheckoutPayload): Observable<CheckoutResponse> {
    const url = `${this.baseUrl}/payment/checkout`;
    return this.httpClient.post<CheckoutResponse>(url, checkoutPayload);
  }

  public submitApplePayPayfort(payload: any, restaurantId: string): Observable<MastercardResponse> {
    const url = `${this.baseUrl}/payment/payfort/applepay/${restaurantId}`;
    return this.httpClient.post<MastercardResponse>(url, payload);
  }

  public submitApplePayTP(payload: ApplePayPaymentPayload): Observable<MastercardResponse> {
    const url = `${this.baseUrl}/payment/total_processing/applepay`;
    return this.httpClient.post<MastercardResponse>(url, payload);
  }

  public getGooglePayPaymentDetails(restaurantId: string) {
    return this.httpClient.get<GooglePayPaymentDetails>(
      `${this.baseUrl}/payment/google-pay/payment-details`,
      { params: { restaurant_id: restaurantId } }
    );
  }

  public validateNiTransaction(mastercardValidationPayload: MastercardValidationPayload): Observable<boolean> {
    const url = `${this.baseUrl}/payment/ni/validate`;
    return this.httpClient.post<boolean>(url, mastercardValidationPayload);
  }

  public getGeideaCheckoutSession(
    payload: MastercardPayload | PayAtTableNiPayload
  ): Observable<GeideaCheckoutSession> {
    const url = `${this.baseUrl}/payment/geidea/session`;
    return this.httpClient.post<GeideaCheckoutSession>(url, payload);
  }

  public validateGeideaCheckout(payload: GeideaValidationPayload): Observable<object> {
    const url = `${this.baseUrl}/payment/geidea/validate`;
    return this.httpClient.post<object>(url, payload);
  }

  public getItemFilters(restaurantId: string): Observable<ItemFilter[]> {
    return this.httpClient.get<ItemFilter[]>(`${this.baseUrl}/item-filter/${restaurantId}`);
  }

  public getWaiterOptions(restaurantId: string): Observable<WaiterOption[]> {
    const url = `${this.baseUrl}/waiter-option/${restaurantId}`;
    return this.httpClient.get<WaiterOption[]>(url);
  }

  public getCurrencies(restaurantId: string): Observable<CurrencyRate[]> {
    const url = `${this.baseUrl}/restaurant/currencies/${restaurantId}`;
    return this.httpClient.get<CurrencyRate[]>(url);
  }

  public getApplePaySession(applePayPayload: ApplePayPayload): Observable<any> {
    const url = `${this.baseUrl}/payment/applepay/session`;
    return this.httpClient.post<any>(url, applePayPayload);
  }

  public getPickupForm(zoneId: string): Observable<PickupFormField[]> {
    const url = `${this.baseUrl}/pickup-form/${zoneId}`;
    return this.httpClient.get<PickupFormField[]>(url);
  }

  public getPayfortSignature(restaurantId: string, payload: MastercardPayload): Observable<PayfortSignature> {
    const url = `${this.baseUrl}/payment/payfort/signature/${restaurantId}`;
    return this.httpClient.post<PayfortSignature>(url, payload);
  }

  public getNGeniusSession(payload: any): Observable<any> {
    const url = `${this.baseUrl}/payment/ngenius/session`;
    return this.httpClient.post(url, payload);
  }

  public getCartConfig(restaurantId: string): Observable<CartConfig> {
    const url = `${this.baseUrl}/config/cart/${restaurantId}`;
    return this.httpClient.get<CartConfig>(url);
  }

  public getTransaction(transactionId: string): Observable<Transaction> {
    const url = `${this.baseUrl}/transaction/${transactionId}`;
    return this.httpClient.get<Transaction>(url);
  }

  public applyPrepaidCheckout(restaurantId: string, payload: VoucherPayload): Observable<VoucherResponse> {
    const url = `${this.baseUrl}/checkout/prepaid/${restaurantId}`;
    return this.httpClient.post<any>(url, payload);
  }

  public applyPostpaidCheckout(restaurantId: string, payload: VoucherPayload): Observable<VoucherResponse> {
    const url = `${this.baseUrl}/checkout/postpaid/${restaurantId}`;
    return this.httpClient.post<any>(url, payload);
  }

  public confirmPostpaidCheckout(restaurantId: string, payload: VoucherPayload): Observable<VoucherResponse> {
    const url = `${this.baseUrl}/checkout/postpaid/${restaurantId}`;
    return this.httpClient.put<any>(url, payload);
  }

  public startEzetapSession(payload: any): Observable<EzeTapSessionDetails> {
    const url = `${this.baseUrl}/payment/ezetap`;
    return this.httpClient.post<any>(url, payload);
  }

  public getEzetapStatus(deviceId: string, transactionId: string): Observable<EzeTapStatusResponse> {
    return this.httpClient
      .get<EzeTapStatusResponse>(`${this.baseUrl}/payment/ezetap?device_id=${deviceId}&transaction_id=${transactionId}`);
  }

  public cancelEzetapPayment(device_id: string, transaction_id: string): Observable<EzeTapStatusResponse> {
    const payload = {
      device_id,
      transaction_id
    };
    return this.httpClient
      .put<EzeTapStatusResponse>
      (`${this.baseUrl}/payment/ezetap`, payload);
  }

  public getTPCheckout(payload: any): Observable<any> {
    const url = `${this.baseUrl}/payment/total_processing`;
    return this.httpClient.post<any>(url, payload);
  }

  public getArbSession(payload): Observable<any> {
    const url = `${this.baseUrl}/payment/arb`;
    return this.httpClient.post<any>(url, payload);
  }

  public getEtisalatSession(payload): Observable<any> {
    const url = `${this.baseUrl}/payment/etisalat`;
    return this.httpClient.post<any>(url, payload);
  }

  public getMagnatiSession(payload): Observable<any> {
    const url = `${this.baseUrl}/payment/magnati`;
    return this.httpClient.post<any>(url, payload);
  }

  public getCCAvenueSession(payload): Observable<SafeHtml> {
    const url = `${this.baseUrl}/payment/ccavenue`;
    return this.httpClient.post(url, payload, {
      responseType: 'text',
      headers: new HttpHeaders().set('Content-Type', 'text/html'),
    });
  }

  public checkMulesoftRoom(restaurantId: string, roomNumber: number): Observable<MulesoftRoomCheckResponse> {
    return this.httpClient.get(`${this.baseUrl}/mulesoft/${restaurantId}/room/${roomNumber}`);
  }

  public getNoonSession(noonPaymentsPayload: NoonPaymentsPayload): Observable<NoonPaymentsResponse> {
    const url = `${this.baseUrl}/payment/noonpayments`;
    return this.httpClient.post<NoonPaymentsResponse>(url, noonPaymentsPayload);
  }

  // Create a Tap/KNET redirect charge
  createTapRedirect(body: {
    subdomain: string;
    order_ids: string[];
    payment_source: 'all' | 'knet';
  }) {
    // You shared a curl earlier to POST /api/payment/tap with { payment_source: 'knet' }
    return this.httpClient.post<{ url: string }>(
      `${this.baseUrl}/payment/tap`,
      body
    );
  }

  // Adyen
  public getAdyenSession(payload: MastercardPayload | PayAtTableNiPayload): Observable<AdyenSessionResponse> {
    const url = `${this.baseUrl}/payment/adyen/session`;
    return this.httpClient.post<AdyenSessionResponse>(url, payload);
  }

  public validateAdyenTransaction(payload: AdyenValidatePayload): Observable<void> {
    const url = `${this.baseUrl}/payment/adyen/validate`;
    return this.httpClient.post<void>(url, payload);
  }

  // Checkout V2
  public getCheckoutV2Session(payload: MastercardPayload | PayAtTableNiPayload): Observable<CheckoutV2SessionResponse> {
    const url = `${this.baseUrl}/v2/payment/checkout`;
    return this.httpClient.post<CheckoutV2SessionResponse>(url, payload);
  }

  public validateCheckoutV2Transaction(payload: CheckoutV2ValidatePayload): Observable<void> {
    const url = `${this.baseUrl}/v2/payment/checkout/validate`;
    return this.httpClient.post<void>(url, payload);
  }

  // Cybersource / Visa Acceptance Unified Checkout
  public getCybersourceSession(payload: MastercardPayload | PayAtTableNiPayload): Observable<CybersourceSessionResponse> {
    const url = `${this.baseUrl}/payment/cybersource`;
    return this.httpClient.post<CybersourceSessionResponse>(url, payload);
  }

  public validateCybersourceTransaction(payload: CybersourceValidatePayload): Observable<void> {
    const url = `${this.baseUrl}/payment/cybersource/validate`;
    return this.httpClient.post<void>(url, payload);
  }
}
